import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Spinner } from '../../components';
import { featureEnabled } from '../../config/features';
import { CloseLineIcon, MoveIcon } from '../../layout/icons';
import { useDraggableChrome } from '../../layout/useDraggableChrome';
import { useQltSlotAttendanceStore } from '../../stores/qlt';
import { useHorizontalOverflow } from '../panels/useHorizontalOverflow';
import { rosterOf } from '../submissions/submissions';
import { useSelectUnit } from '../useSelectUnit';
import { ExamHtml, ExamStudentSheet } from './ExamStudentSheet';
import { buildExamResults, durationSeconds, examCardView, examDisplayTitle, formatDuration, formatScore, groupExams, topWrongQuestions, wrongPercent, RANK_BADGE_COLOR, STATUS_COLORS, type ExamSlice } from './examResults';
import { useExamResultStore } from './store';
import '../../styles/rail-drag.css';
import '../../styles/quiz.css';
import '../../styles/submissions.css';

const DONUT_RADIUS = 30;
const DONUT_BOX = 76;
const DONUT_CENTRE = DONUT_BOX / 2;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;

/** Hand-rolled multi-slice doughnut; the repo carries no chart library. */
function Donut({ slices }: { slices: ExamSlice[] }) {
  const total = slices.reduce((sum, slice) => sum + slice.count, 0);
  const drawn = total > 0 ? slices.filter((slice) => slice.count > 0) : [{ title: 'Chưa có dữ liệu', count: 1, color: STATUS_COLORS.idle }];
  const span = total > 0 ? total : 1;
  let consumed = 0;
  return (
    <svg className="quiz-results__donut-svg" width={DONUT_BOX} height={DONUT_BOX} viewBox={`0 0 ${DONUT_BOX} ${DONUT_BOX}`} aria-hidden={true}>
      {drawn.map((slice) => {
        const length = (DONUT_CIRCUMFERENCE * slice.count) / span;
        const offset = consumed;
        consumed += length;
        return <circle key={slice.title} cx={DONUT_CENTRE} cy={DONUT_CENTRE} r={DONUT_RADIUS} fill="none" stroke={slice.color} strokeWidth="9" strokeDasharray={`${length} ${DONUT_CIRCUMFERENCE - length}`} strokeDashoffset={-offset} transform={`rotate(-90 ${DONUT_CENTRE} ${DONUT_CENTRE})`} />;
      })}
    </svg>
  );
}

function DonutRow({ slices }: { slices: ExamSlice[] }) {
  return (
    <div className="quiz-results__donut-row">
      <div className="quiz-results__donut"><Donut slices={slices} /></div>
      <ul className="quiz-results__legend">
        {slices.filter((slice) => slice.count > 0).map((slice) => (
          <li key={slice.title} className="quiz-results__legend-row">
            <span className="quiz-results__legend-dot" style={{ background: slice.color }} />
            <span className="quiz-results__legend-title">{slice.title}</span>
            <span className="quiz-results__legend-count">{slice.count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Real class results for one IZ exam. The board is projected, so names, scores and who has not
// submitted wait behind Hiện điểm; counts, the status ring and the question tallies are anonymous.
export function ExamResultsCard() {
  const examId = useExamResultStore((state) => state.examId);
  const attempts = useExamResultStore((state) => state.attempts);
  const titles = useExamResultStore((state) => state.courseTitles);
  const examTitles = useExamResultStore((state) => state.examTitles);
  const questions = useExamResultStore((state) => state.questions);
  const questionsLoading = useExamResultStore((state) => state.questionsLoading);
  const questionsError = useExamResultStore((state) => state.questionsError);
  const showScores = useExamResultStore((state) => state.showScores);
  const setShowScores = useExamResultStore((state) => state.setShowScores);
  const open = useExamResultStore((state) => state.open);
  const close = useExamResultStore((state) => state.close);
  const examUnits = useExamResultStore((state) => state.examUnits);
  const selectUnit = useSelectUnit();
  const boardUnitId = new URLSearchParams(useLocation().search).get('unit');
  const rows = useQltSlotAttendanceStore((state) => state.rows);
  const rosterLoading = useQltSlotAttendanceStore((state) => state.loading);
  const loading = useExamResultStore((state) => state.loading);
  const loadError = useExamResultStore((state) => state.error);
  const drag = useDraggableChrome({ id: 'exam-results', enabled: true });

  const roster = useMemo(() => rosterOf(rows), [rows]);
  // a test unit opens this before its class's attempts loaded, or with none at all
  const view = useMemo(() => (examId ? examCardView(groupExams(attempts, roster), examId, { loading: loading || (rosterLoading && roster.length === 0), error: loadError }) : null), [attempts, roster, examId, loading, rosterLoading, loadError]);
  const exam = view && view.kind === 'ready' ? view.exam : null;
  const results = useMemo(() => (exam ? buildExamResults(exam, roster, questions) : null), [exam, roster, questions]);

  const [picked, setPicked] = useState(0);
  // Bài làm từng em: the leader index being viewed; names stay behind Hiện điểm, so hiding scores closes it
  const [sheetIndex, setSheetIndex] = useState<number | null>(null);
  useEffect(() => { setPicked(0); setSheetIndex(null); }, [examId]);
  useEffect(() => { if (!showScores) setSheetIndex(null); }, [showScores]);
  const sheetOrder = useMemo(() => (exam && results ? results.leaders.map((row) => exam.byCode[row.code]).filter(Boolean) : []), [exam, results]);
  const names = useMemo(() => {
    const out: Record<string, string> = {};
    roster.forEach((student) => { out[student.code] = student.name; });
    return out;
  }, [roster]);
  const count = results ? results.questions.length : 0;
  const { ref: chipsRef, faded: chipsFaded, measure: measureChips } = useHorizontalOverflow(count);
  const questionIndex = picked < count ? picked : 0;

  // keep the pick reachable without dragging the strip; scrollIntoView would drag the card with it
  useEffect(() => {
    const track = chipsRef.current;
    const chip = track && questionIndex < track.children.length ? track.children[questionIndex] as HTMLElement : null;
    if (!track || !chip) return;
    const right = chip.offsetLeft + chip.offsetWidth;
    if (chip.offsetLeft < track.scrollLeft) track.scrollLeft = chip.offsetLeft - 6;
    else if (right > track.scrollLeft + track.clientWidth) track.scrollLeft = right - track.clientWidth + 6;
    measureChips();
  }, [questionIndex, chipsRef, measureChips]);

  if (!examId) return null;
  // already on the board when Kết quả opened it from the unit's own rail
  const unitRef = featureEnabled('board.testUnit') && examUnits[examId] && examUnits[examId].unitId !== boardUnitId ? examUnits[examId] : undefined;
  // the same path as picking the unit in Kho bài
  const openOnBoard = () => {
    if (!unitRef) return;
    close();
    selectUnit({ unitId: unitRef.unitId, unitName: unitRef.title || 'Bài kiểm tra', courseId: unitRef.courseId });
  };
  const className = ['quiz-results quiz-results--floating', drag.dragging ? 'quiz-results--dragging' : ''].filter(Boolean).join(' ');
  const question = results ? results.questions[questionIndex] : null;
  const answered = question ? question.countCorrectSelections + question.countWrongSelections : 0;
  const hardest = results ? topWrongQuestions(results.questions, 3) : [];
  const questionNote = questionsLoading ? <Spinner size="small" label="Đang tải câu hỏi…" /> : questionsError
    ? <p className="quiz-results__empty">Chưa tải được câu hỏi: {questionsError} <button type="button" className="hw-panel__refresh" onClick={() => void open(examId)}>Thử lại</button></p>
    : null;

  return (
    <aside ref={drag.ref} className={className} aria-label="Kết quả bài kiểm tra">
      <header className="quiz-results__header">
        <button type="button" className="quiz-results__handle" aria-label="Kéo để di chuyển bảng kết quả, dùng phím mũi tên để chỉnh vị trí" {...drag.handleProps}>
          <MoveIcon size={18} />
        </button>
        <div className="quiz-results__heading">
          <div className="quiz-results__title">Kết quả</div>
          <div className="quiz-results__subtitle">{exam ? [examDisplayTitle(exam, examTitles), titles[exam.courseId] || ''].filter(Boolean).join(' • ') : ''}</div>
        </div>
        <button type="button" className="hw-panel__refresh" aria-pressed={showScores} onClick={() => setShowScores(!showScores)}>{showScores ? 'Ẩn điểm' : 'Hiện điểm'}</button>
        <button type="button" className="quiz-results__close" aria-label="Đóng bảng kết quả" onClick={close}>
          <CloseLineIcon size={18} />
        </button>
      </header>

      {!results ? (
        <div className="quiz-results__scroll">{view && view.kind === 'error' ? <p className="quiz-results__empty">Chưa tải được kết quả: {view.message}</p> : <Spinner label="Đang tải kết quả của lớp…" />}</div>
      ) : showScores && sheetIndex !== null && sheetOrder[sheetIndex] ? (
        <div className="quiz-results__scroll">
          <ExamStudentSheet order={sheetOrder} index={sheetIndex} names={names} questions={questions} onStep={setSheetIndex} onBack={() => setSheetIndex(null)} />
        </div>
      ) : (
        <div className="quiz-results__scroll">
          {unitRef ? <div className="quiz-results__actions"><button type="button" className="hw-panel__refresh" onClick={openOnBoard}>Mở đề lên bảng</button></div> : null}
          <section className="quiz-results__block">
            <h3 className="quiz-results__block-title">Số học sinh đã nộp bài</h3>
            <p className="quiz-results__stat">
              <span className="quiz-results__stat-value">{results.submitted} học sinh</span>
              <span className="quiz-results__stat-total"> / {results.total}</span>
            </p>
            <div className="quiz-results__meter">
              <span className="quiz-results__meter-fill" style={{ width: `${results.total > 0 ? Math.round((results.submitted / results.total) * 100) : 0}%` }} />
            </div>
          </section>

          <section className="quiz-results__block">
            <h3 className="quiz-results__block-title">Tỉ lệ trạng thái làm bài</h3>
            <DonutRow slices={results.status} />
          </section>

          <section className="quiz-results__block">
            <h3 className="quiz-results__block-title">Câu hỏi sai nhiều nhất</h3>
            {questionNote}
            {hardest.length === 0 ? <p className="quiz-results__empty">Chưa có câu nào bị làm sai.</p> : hardest.map((stat) => (
              <div key={stat.id} className="quiz-results__wrong">
                <div className="quiz-results__wrong-title">{stat.title}</div>
                <div className="quiz-results__wrong-figures">
                  <span className="quiz-results__wrong-count">{stat.countWrongSelections}</span>
                  <span className="quiz-results__wrong-percent">{wrongPercent(stat)}%</span>
                </div>
                <div className="quiz-results__wrong-track"><span className="quiz-results__wrong-fill" style={{ width: `${wrongPercent(stat)}%` }} /></div>
                <div className="quiz-results__wrong-captions">
                  <span className="quiz-results__caption">Trả lời sai</span>
                  <span className="quiz-results__caption">Trên số học sinh trả lời</span>
                </div>
              </div>
            ))}
          </section>

          <section className="quiz-results__block">
            <h3 className="quiz-results__block-title">Nội dung câu hỏi</h3>
            <div className="h-scroll quiz-results__chips">
              <div className="h-scroll__track quiz-results__chip-track" ref={chipsRef} onScroll={measureChips} role="group" aria-label="Chọn câu hỏi để xem thống kê">
                {results.questions.map((item, index) => (
                  <button key={item.id} type="button" className={index === questionIndex ? 'quiz-results__chip quiz-results__chip--active' : 'quiz-results__chip'} aria-pressed={index === questionIndex} onClick={() => setPicked(index)}>Câu {index + 1}</button>
                ))}
              </div>
              {chipsFaded ? <span className="h-scroll__fade" aria-hidden={true} /> : null}
            </div>
            <p className="quiz-results__stem">{question ? (question.stemHtml ? <>Câu {question.index + 1}: <ExamHtml html={question.stemHtml} /></> : question.title) : 'Bài kiểm tra này chưa có câu nào.'}</p>
            <h3 className="quiz-results__block-title quiz-results__block-title--sub">Thống kê câu trả lời</h3>
            {!question || answered + question.blank === 0 ? <p className="quiz-results__empty">Chưa có học sinh nào trả lời câu này.</p> : question.options.length > 0 ? question.options.map((option) => {
              const percent = answered > 0 ? Math.round((option.count / answered) * 100) : 0;
              return (
                <div key={option.key} className="quiz-results__option">
                  <span className={option.correct ? 'quiz-results__option-badge quiz-results__option-badge--correct' : 'quiz-results__option-badge'}>{option.label}</span>
                  <span className="quiz-results__option-body">
                    <span className="quiz-results__option-head">
                      <span className="quiz-results__option-text">{option.text || /<img\b/i.test(option.html) ? <ExamHtml html={option.html} /> : (option.correct ? 'Đáp án đúng' : '')}</span>
                      <span className="quiz-results__option-count">{option.count} / {answered}</span>
                    </span>
                    <span className="quiz-results__option-track"><span className={option.correct ? 'quiz-results__option-fill quiz-results__option-fill--correct' : 'quiz-results__option-fill'} style={{ width: `${percent}%` }} /></span>
                  </span>
                  <span className={option.correct ? 'quiz-results__option-percent quiz-results__option-percent--correct' : 'quiz-results__option-percent'}>{percent}%</span>
                </div>
              );
            }) : (
              <DonutRow slices={[
                { title: 'Đúng', count: question.countCorrectSelections, color: STATUS_COLORS.done },
                { title: 'Sai', count: question.countWrongSelections, color: '#EF4444' },
                { title: 'Bỏ trống', count: question.blank, color: STATUS_COLORS.idle },
              ]} />
            )}
            {question && question.options.length > 0 && question.blank > 0 ? <p className="quiz-results__empty">{question.blank} học sinh bỏ trống câu này.</p> : null}
          </section>

          {showScores ? (
            <>
              <section className="quiz-results__block">
                <h3 className="quiz-results__block-title">Phân bố điểm</h3>
                <p className="quiz-results__stat">
                  <span className="quiz-results__stat-value">{formatScore(results.averageScore)}/10</span>
                  <span className="quiz-results__stat-total"> điểm trung bình</span>
                </p>
                {results.leaders.length === 0 ? <p className="quiz-results__empty">Chưa có điểm</p> : <DonutRow slices={results.distribution} />}
              </section>
              <section className="quiz-results__block">
                <h3 className="quiz-results__block-title">Xếp hạng</h3>
                {results.leaders.length === 0 ? <p className="quiz-results__empty">Chưa có học sinh nào nộp bài.</p> : (
                  <ol className="quiz-results__ranks">
                    {results.leaders.map((row, index) => (
                      <li key={row.code} className="quiz-results__rank-row">
                        <button type="button" className="quiz-results__rank-open" aria-label={`Xem bài làm của ${row.name}`} onClick={() => setSheetIndex(index)}>
                          <span className="quiz-results__rank-badge" style={{ background: RANK_BADGE_COLOR }}>{row.rank}</span>
                          <span className="quiz-results__rank-name">{row.name}</span>
                          <span className="quiz-results__rank-meta">
                            <span className="quiz-results__rank-score">{formatScore(row.score)}</span>
                            <span className="quiz-results__rank-time">{formatDuration(durationSeconds(row))}</span>
                          </span>
                          <span className="quiz-results__rank-hint">Xem bài làm</span>
                        </button>
                      </li>
                    ))}
                  </ol>
                )}
              </section>
              <section className="quiz-results__block">
                <h3 className="quiz-results__block-title">Chưa nộp bài</h3>
                {results.notSubmitted.length === 0 ? <p className="quiz-results__empty">Cả lớp đã nộp bài.</p> : (
                  <ul className="quiz-results__ranks">
                    {results.notSubmitted.map((row) => (
                      <li key={row.code} className="quiz-results__rank-row">
                        <span className="quiz-results__rank-name">{row.name}</span>
                        <span className={row.status === 'doing' ? 'hw-status hw-status--submitted' : 'hw-status hw-status--missing'}>{row.status === 'doing' ? 'Đang làm' : 'Chưa làm'}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          ) : (
            <section className="quiz-results__block">
              <p className="quiz-results__empty">Điểm, xếp hạng và danh sách chưa nộp đang ẩn vì bảng đang chiếu cho cả lớp. Bấm Hiện điểm để xem.</p>
            </section>
          )}
        </div>
      )}
    </aside>
  );
}
